<script setup lang="ts">
import { PhChatCircleDots, PhHeart } from '@phosphor-icons/vue'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  shallowRef,
  useTemplateRef,
  ref,
  watch,
} from 'vue'
import { useRouter } from 'vue-router'
import {
  DISTRIBUTE_ANCHOR_LABEL,
  DISTRIBUTE_BGM_DEFAULT_COVER_URL,
  DISTRIBUTE_LIKE_FEEDBACK_DURATION,
  DISTRIBUTE_UNSUPPORTED_MESSAGE,
} from '@/constants/distribute'
import { useDistributeFeedStore } from '@/stores/distributeFeed'
import { formatNumber } from '@/utils/number'
import {
  buildAnchorUrl,
  buildCartoonUrl,
  buildLandingUrl,
  openAnchor,
  openExternal,
} from '@/utils/distribute/navigation'
import { optimizeAvatarUrl } from '@/utils/distribute/media-url'
import { useElementWidth } from '@/composables/useElementWidth'
import { useToast } from '@/composables/useToast'
import type { Emits, Props } from './type'

defineOptions({
  name: 'DistributeCoverMetaOverlay',
})

const props = withDefaults(defineProps<Props>(), {
  showAnchor: false,
  anchor: null,
  anchorLabel: DISTRIBUTE_ANCHOR_LABEL,
  showBgm: false,
  requireLogin: false,
})
const emit = defineEmits<Emits>()

const router = useRouter()
const feedStore = useDistributeFeedStore()
const toast = useToast()
const likePending = ref(false)
const likeFeedback = ref(false)
const isCartoonStackExpanded = ref(false)
const singleRoleNameWidth = shallowRef(0)
const bottomRef = useTemplateRef<HTMLElement>('bottom')
const statsRef = useTemplateRef<HTMLElement>('stats')
const singleRoleNameRef = useTemplateRef<HTMLElement>('singleRoleName')
const { width: bottomWidth } = useElementWidth(bottomRef, 0)
const { width: statsWidth } = useElementWidth(statsRef, 0)
let feedbackTimer: number | null = null

const SINGLE_ROLE_NAME_BASE_WIDTH = 60
const SINGLE_ROLE_NAME_MAX_WIDTH = 150
const SINGLE_ROLE_NAME_SAFE_GAP = 12

const likeCount = computed(() => props.model.item.likeNum)
const formattedLikeCount = computed(() => formatNumber(likeCount.value))
const formattedCommentCount = computed(() => formatNumber(props.model.item.commentNum))
const isLiked = computed(() => Boolean(props.model.item.liked))
const relCartoons = computed(() => props.model.item.relCartoons ?? [])
const hiddenCount = computed(() => Math.max(0, relCartoons.value.length - 3))
const hasManyCartoons = computed(() => relCartoons.value.length > 1)
const singleCartoon = computed(() => relCartoons.value[0] ?? null)
const singleCartoonAvatar = computed(() => optimizeAvatarUrl(singleCartoon.value?.avatar))
const shouldShowAnchor = computed(() => props.showAnchor)
const bgmCover = computed(() => {
  if (props.model.item.bgmLib) {
    return props.model.item.bgmLib.cover || DISTRIBUTE_BGM_DEFAULT_COVER_URL
  }

  return props.model.coverUrl
})
const shouldShowBgm = computed(() =>
  Boolean((props.showBgm || props.model.item.bgmLib) && bgmCover.value),
)
const singleRoleRequiredWidth = computed(() =>
  Math.min(SINGLE_ROLE_NAME_MAX_WIDTH, SINGLE_ROLE_NAME_BASE_WIDTH + singleRoleNameWidth.value),
)
const availableRoleWidth = computed(() =>
  Math.max(0, bottomWidth.value - statsWidth.value - SINGLE_ROLE_NAME_SAFE_GAP),
)
const shouldHideSingleRoleName = computed(
  () =>
    Boolean(singleCartoon.value && !hasManyCartoons.value) &&
    bottomWidth.value > 0 &&
    statsWidth.value > 0 &&
    availableRoleWidth.value < singleRoleRequiredWidth.value,
)
const commentUrl = computed(() => {
  const landingUrl = buildLandingUrl(props.model.item)

  if (!landingUrl) {
    return null
  }

  const url = new URL(landingUrl)
  url.searchParams.set('comment', '1')
  url.searchParams.set('activateComment', '1')

  return url.toString()
})

watch(
  () => props.model.item.id,
  () => {
    likePending.value = false
    likeFeedback.value = false
    isCartoonStackExpanded.value = false

    if (feedbackTimer) {
      window.clearTimeout(feedbackTimer)
      feedbackTimer = null
    }
  },
)

onBeforeUnmount(() => {
  if (feedbackTimer) {
    window.clearTimeout(feedbackTimer)
  }
})

function measureSingleRoleNameWidth() {
  const nextWidth = singleRoleNameRef.value?.scrollWidth ?? 0

  if (nextWidth > 0) {
    singleRoleNameWidth.value = nextWidth
  }
}

watch(
  () => singleCartoon.value?.name,
  () => {
    singleRoleNameWidth.value = 0
    void nextTick(measureSingleRoleNameWidth)
  },
  { immediate: true, flush: 'post' },
)

onMounted(() => {
  void nextTick(measureSingleRoleNameWidth)
})

function openRelCartoon(index: number) {
  if (requestLogin()) {
    return
  }

  const cartoon = relCartoons.value[index]
  const url = buildCartoonUrl(cartoon)

  if (!url) {
    toast.info(DISTRIBUTE_UNSUPPORTED_MESSAGE)
    return
  }

  void router.push(url)
}

function openOverlayAnchor() {
  if (requestLogin()) {
    return
  }

  if (props.anchor) {
    if (!buildAnchorUrl(props.anchor)) {
      toast.info(DISTRIBUTE_UNSUPPORTED_MESSAGE)
      return
    }

    openAnchor(props.anchor)
    return
  }

  const landingUrl = buildLandingUrl(props.model.item)

  if (!landingUrl) {
    toast.info(DISTRIBUTE_UNSUPPORTED_MESSAGE)
    return
  }

  openExternal(landingUrl)
}

async function toggleLike() {
  if (requestLogin()) {
    return
  }

  if (likePending.value) {
    return
  }

  likePending.value = true
  likeFeedback.value = true

  if (feedbackTimer) {
    window.clearTimeout(feedbackTimer)
  }

  feedbackTimer = window.setTimeout(() => {
    likeFeedback.value = false
    feedbackTimer = null
  }, DISTRIBUTE_LIKE_FEEDBACK_DURATION)

  try {
    await feedStore.toggleItemLike(props.model.item.id)
  } catch (error) {
    toast.info(error instanceof Error ? error.message : '点赞操作失败')
  } finally {
    likePending.value = false
  }
}

function openComments() {
  if (requestLogin()) {
    return
  }

  if (!commentUrl.value) {
    toast.info(DISTRIBUTE_UNSUPPORTED_MESSAGE)
    return
  }

  openExternal(commentUrl.value)
}

function expandCartoonStack() {
  isCartoonStackExpanded.value = true
}

function collapseCartoonStack(event?: FocusEvent) {
  if (
    event?.currentTarget instanceof HTMLElement &&
    event.relatedTarget instanceof Node &&
    event.currentTarget.contains(event.relatedTarget)
  ) {
    return
  }

  isCartoonStackExpanded.value = false
}

function requestLogin() {
  if (!props.requireLogin) {
    return false
  }

  emit('login')
  return true
}

function resolveCartoonAvatar(src: string | null | undefined): string | undefined {
  return optimizeAvatarUrl(src) ?? undefined
}
</script>

<template>
  <div class="cover-meta">
    <button
      v-if="shouldShowAnchor"
      type="button"
      class="cover-meta__anchor"
      @click.stop="openOverlayAnchor"
    >
      {{ anchor?.name || anchorLabel }}
    </button>

    <div ref="bottom" class="cover-meta__bottom">
      <div class="cover-meta__roles">
        <button
          v-if="singleCartoon && !hasManyCartoons"
          type="button"
          class="cover-meta__single-role"
          :class="{ 'is-name-hidden': shouldHideSingleRoleName }"
          @click.stop="openRelCartoon(0)"
        >
          <span class="cover-meta__single-pill">
            <img
              class="cover-meta__avatar"
              :src="singleCartoonAvatar ?? undefined"
              :alt="singleCartoon.name || '角色头像'"
            />
            <span ref="singleRoleName" class="cover-meta__single-role-name">
              {{ singleCartoon.name || '角色' }}
            </span>
          </span>
        </button>

        <div
          v-else-if="relCartoons.length"
          class="cover-meta__stack"
          :class="{ 'is-overflow': relCartoons.length > 3, 'is-expanded': isCartoonStackExpanded }"
          aria-label="相关角色"
          @mouseenter="expandCartoonStack"
          @mouseleave="collapseCartoonStack()"
          @focusin="expandCartoonStack"
          @focusout="collapseCartoonStack"
        >
          <button
            v-for="(cartoon, index) in relCartoons"
            :key="cartoon.id || index"
            type="button"
            class="cover-meta__stack-avatar"
            :class="{ 'is-collapsed-extra': index > 2 }"
            :style="{ zIndex: String(index + 1) }"
            @click.stop="openRelCartoon(index)"
          >
            <img :src="resolveCartoonAvatar(cartoon.avatar)" :alt="cartoon.name || '角色头像'" />
            <span v-if="index === 2 && hiddenCount > 0" class="cover-meta__stack-more"
              >+{{ hiddenCount }}</span
            >
          </button>
        </div>
      </div>

      <div ref="stats" class="cover-meta__stats">
        <button
          type="button"
          class="cover-meta__stat cover-meta__like"
          :class="{ 'is-liked': isLiked, 'is-pending': likePending, 'is-feedback': likeFeedback }"
          :aria-pressed="isLiked"
          @click.stop="toggleLike"
        >
          <PhHeart :size="20" :weight="isLiked ? 'fill' : 'regular'" />
          {{ formattedLikeCount }}
          <span class="cover-meta__like-pop" aria-hidden="true">
            <PhHeart :size="16" weight="fill" />
          </span>
        </button>
        <button
          type="button"
          class="cover-meta__stat cover-meta__comment"
          @click.stop="openComments"
        >
          <PhChatCircleDots :size="20" />
          {{ formattedCommentCount }}
        </button>
        <img
          v-if="shouldShowBgm"
          class="cover-meta__bgm"
          :src="bgmCover ?? undefined"
          alt=""
          aria-hidden="true"
          draggable="false"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.cover-meta {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 58%, rgba(0, 0, 0, 0.5) 100%);
}

.cover-meta__anchor,
.cover-meta__bottom,
.cover-meta__roles,
.cover-meta__stats,
.cover-meta__stat,
.cover-meta__single-role,
.cover-meta__stack,
.cover-meta__stack-avatar {
  pointer-events: auto;
}

.cover-meta__anchor {
  position: absolute;
  right: 50%;
  bottom: 54px;
  display: inline-flex;
  min-height: 35px;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  transform: translateX(50%);
  backdrop-filter: blur(5px);
  cursor: pointer;
  overflow: hidden;
  transition: transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.cover-meta__anchor::after {
  content: '';
  position: absolute;
  top: -40%;
  bottom: -40%;
  left: -45%;
  width: 34%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.72),
    rgba(255, 255, 255, 0)
  );
  opacity: 0;
  transform: translateX(-140%) rotate(16deg);
}

.cover-meta:hover .cover-meta__anchor::after {
  animation: anchor-sheen 1.1s ease-out;
}

.cover-meta__anchor:hover,
.cover-meta__anchor:focus-visible {
  transform: translateX(50%) scale(var(--app-interaction-object-scale-hover-subtle));
}

.cover-meta__anchor:active {
  transform: translateX(50%) scale(var(--app-interaction-object-scale-active-subtle));
}

.cover-meta__bottom {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 44px;
}

.cover-meta__roles {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 3;
  min-width: 0;
}

.cover-meta__single-role,
.cover-meta__stack {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  padding: 8px;
}

.cover-meta__single-role {
  max-width: 150px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transform-origin: left center;
  transition:
    filter 0.16s ease,
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.cover-meta__single-pill {
  display: inline-flex;
  height: 28px;
  min-width: 0;
  align-items: center;
  gap: 6px;
  padding-right: 10px;
  border-radius: 16px;
  transition: text-shadow 0.16s ease;
}

.cover-meta__single-role:hover,
.cover-meta__single-role:focus-visible {
  transform: scale(var(--app-interaction-object-scale-hover));
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.16));
}

.cover-meta__single-role:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.cover-meta__single-role:hover .cover-meta__single-pill,
.cover-meta__single-role:focus-visible .cover-meta__single-pill,
.cover-meta__roles:hover .cover-meta__single-pill {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.cover-meta__single-role-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cover-meta__single-role.is-name-hidden .cover-meta__single-pill {
  gap: 0;
  padding-right: 0;
}

.cover-meta__single-role.is-name-hidden .cover-meta__single-role-name {
  display: none;
}

.cover-meta__avatar,
.cover-meta__stack-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
}

.cover-meta__avatar,
.cover-meta__stack-avatar img {
  object-fit: cover;
  background: rgba(255, 255, 255, 0.24);
}

.cover-meta__stack {
  position: relative;
  isolation: isolate;
  padding-right: 14px;
  --stack-offset: -2px;
  --stack-mask: radial-gradient(circle 14px at -9.8px 50%, transparent 99%, white 100%);
  --stack-scale: 1;
  transition:
    padding-right 0.18s ease,
    filter 0.18s ease;
}

.cover-meta__stack-avatar {
  position: relative;
  overflow: hidden;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  cursor: pointer;
  transform-origin: center;
  transform: scale(var(--stack-scale));
  transition:
    flex-basis 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
    width 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
    margin-left 0.22s cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 0.16s ease,
    transform var(--app-interaction-duration) var(--app-interaction-ease),
    box-shadow 0.16s ease;
  will-change: width, margin-left, transform;
}

.cover-meta__stack-avatar:not(:first-child) {
  margin-left: var(--stack-offset);
  mask-image: var(--stack-mask);
  -webkit-mask-image: var(--stack-mask);
}

.cover-meta__stack-avatar.is-collapsed-extra {
  flex-basis: 0;
  width: 0;
  margin-left: 0;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-8px) scale(0.98);
}

.cover-meta__stack.is-expanded,
.cover-meta__stack.is-overflow:hover,
.cover-meta__stack.is-overflow:focus-within,
.cover-meta__roles:hover .cover-meta__stack.is-overflow {
  padding-right: 10px;
  --stack-offset: 0;
  --stack-scale: 1.03;
}

.cover-meta__stack.is-expanded .cover-meta__stack-avatar.is-collapsed-extra,
.cover-meta__stack.is-overflow:hover .cover-meta__stack-avatar.is-collapsed-extra,
.cover-meta__stack.is-overflow:focus-within .cover-meta__stack-avatar.is-collapsed-extra,
.cover-meta__roles:hover
  .cover-meta__stack.is-overflow
  .cover-meta__stack-avatar.is-collapsed-extra {
  flex-basis: 28px;
  width: 28px;
  margin-left: var(--stack-offset);
  opacity: 1;
  pointer-events: auto;
  transform: scale(var(--stack-scale));
}

.cover-meta__stack.is-expanded .cover-meta__stack-more,
.cover-meta__stack.is-overflow:hover .cover-meta__stack-more,
.cover-meta__stack.is-overflow:focus-within .cover-meta__stack-more,
.cover-meta__roles:hover .cover-meta__stack.is-overflow .cover-meta__stack-more {
  opacity: 0;
}

.cover-meta__stack-avatar:hover,
.cover-meta__stack-avatar:focus-visible {
  z-index: 20 !important;
  transform: scale(calc(var(--stack-scale) * var(--app-interaction-object-scale-hover)));
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.22);
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.22));
}

.cover-meta__stack-avatar:active {
  transform: scale(calc(var(--stack-scale) * var(--app-interaction-object-scale-active)));
}

.cover-meta__stack-avatar img {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.cover-meta__stack-more {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.34);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
  transition: opacity 0.12s ease;
}

.cover-meta__stats {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0;
  padding: 8px;
  color: #fff;
}

.cover-meta__stat {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-radius: 29px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.cover-meta__like,
.cover-meta__comment {
  cursor: pointer;
  transform-origin: center;
  transition:
    transform var(--app-interaction-duration) var(--app-interaction-ease),
    opacity 0.16s ease;
}

.cover-meta__like:hover,
.cover-meta__like:focus-visible,
.cover-meta__comment:hover,
.cover-meta__comment:focus-visible {
  transform: scale(var(--app-interaction-object-scale-hover));
}

.cover-meta__like:active,
.cover-meta__comment:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.cover-meta__like.is-pending {
  opacity: 0.72;
}

.cover-meta__like.is-feedback {
  animation: like-feedback 0.56s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.cover-meta__like-pop {
  position: absolute;
  left: 10px;
  top: 50%;
  color: #fff;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.5);
  pointer-events: none;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.22));
}

.cover-meta__like.is-feedback .cover-meta__like-pop {
  animation: like-pop 0.52s ease-out;
}

.cover-meta__bgm {
  width: 28px;
  height: 28px;
  margin-left: 6px;
  pointer-events: none;
  border-radius: 999px;
  animation: bgm-spin 2.4s linear infinite;
  animation-play-state: paused;
  object-fit: cover;
  transform-origin: center;
}

.cover-meta:hover .cover-meta__bgm {
  animation-play-state: running;
}

@media (max-width: 520px) {
  .cover-meta__single-role-name {
    display: none;
  }
}

@keyframes anchor-sheen {
  0% {
    opacity: 0;
    transform: translateX(-140%) rotate(16deg);
  }

  18% {
    opacity: 0.8;
  }

  100% {
    opacity: 0;
    transform: translateX(520%) rotate(16deg);
  }
}

@keyframes like-feedback {
  0% {
    transform: scale(0.94);
  }

  34% {
    transform: scale(1.2);
  }

  62% {
    transform: scale(1.03);
  }

  100% {
    transform: scale(var(--app-interaction-object-scale-hover));
  }
}

@keyframes like-pop {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.5);
  }

  22% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -170%) scale(1.25);
  }
}

@keyframes bgm-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cover-meta:hover .cover-meta__anchor::after,
  .cover-meta:hover .cover-meta__bgm,
  .cover-meta__like.is-feedback,
  .cover-meta__like.is-feedback .cover-meta__like-pop {
    animation: none;
  }
}
</style>
